import { useState } from 'react';
import { Outlet, NavLink, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { motion } from 'motion/react';
import { Users, ArrowLeft, Menu, X, LogOut, ShieldCheck } from 'lucide-react';

import { LanguageSwitcher } from '@/components/common/LanguageSwitcher';
import { ThemeToggle } from '@/components/common/ThemeToggle';
import { Wordmark } from '@/components/common/Wordmark';
import { Avatar } from '@/components/ui/Avatar';
import { useAuthStore } from '@/stores/authStore';
import { logoutOwner } from '@/services/authService';
import { cn } from '@/lib/utils';

export function AdminShell() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [signingOut, setSigningOut] = useState(false);

  async function handleLogout() {
    try {
      setSigningOut(true);
      await logoutOwner();
      navigate('/', { replace: true });
    } catch (err) {
      toast.error(t('errors.genericError'), {
        description: err instanceof Error ? err.message : undefined,
      });
    } finally {
      setSigningOut(false);
    }
  }

  const navLinkClass = ({ isActive }: { isActive: boolean }) =>
    cn(
      'relative flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-all duration-300 ease-soft',
      isActive ? 'bg-primary/[0.1] text-primary' : 'text-muted hover:bg-faint hover:text-ink',
    );

  return (
    <div className="flex h-screen overflow-hidden bg-bg">
      {/* SIDEBAR */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 flex w-72 transform flex-col border-r border-border bg-card transition-transform duration-200 md:static md:translate-x-0',
          mobileOpen ? 'translate-x-0' : '-translate-x-full',
        )}
        aria-label="Sidebar"
      >
        <div className="flex items-center justify-between px-5 pb-2 pt-6">
          <Link to="/admin">
            <Wordmark />
          </Link>
          <button
            type="button"
            onClick={() => setMobileOpen(false)}
            className="cursor-pointer rounded-full p-2 text-muted transition-colors hover:bg-faint md:hidden"
            aria-label={t('common.close')}
          >
            <X size={18} />
          </button>
        </div>

        <div className="mx-3 mt-5 flex items-center gap-2.5 rounded-2xl border border-primary/20 bg-primary/[0.06] px-3 py-2.5 text-primary">
          <ShieldCheck size={18} />
          <p className="text-sm font-medium">{t('admin.title')}</p>
        </div>

        <nav className="flex-1 space-y-1 overflow-y-auto px-3 py-4">
          <p className="px-3 text-[10px] font-medium uppercase tracking-[0.18em] text-muted/80">
            {t('admin.title')}
          </p>
          <NavLink
            to="/admin"
            end
            onClick={() => setMobileOpen(false)}
            className={navLinkClass}
          >
            {({ isActive }) => (
              <>
                {isActive && (
                  <motion.span
                    layoutId="admin-active-bar"
                    className="absolute inset-y-0 left-0 my-auto h-5 w-1 rounded-r-full bg-primary"
                    transition={{ type: 'spring', stiffness: 400, damping: 35 }}
                  />
                )}
                <Users size={18} />
                {t('admin.merchants')}
              </>
            )}
          </NavLink>
        </nav>

        <div className="mx-3 mb-3">
          <Link
            to="/dashboard"
            onClick={() => setMobileOpen(false)}
            className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-muted transition-all duration-300 ease-soft hover:bg-faint hover:text-ink"
          >
            <ArrowLeft size={18} />
            {t('admin.backToApp')}
          </Link>
        </div>

        <div className="mx-3 mb-4 rounded-2xl border border-border bg-faint p-3">
          <div className="flex items-center gap-2.5">
            <Avatar name={user?.displayName ?? user?.email ?? '?'} size={32} />
            <div className="min-w-0 flex-1">
              <p className="truncate text-xs font-medium">
                {user?.displayName ?? t('common.appName')}
              </p>
              <p className="truncate text-[10px] text-muted">{user?.email}</p>
            </div>
            <button
              type="button"
              onClick={handleLogout}
              disabled={signingOut}
              className="cursor-pointer rounded-full p-2 text-muted transition-colors hover:bg-card hover:text-danger disabled:opacity-50"
              aria-label={t('settings.logout')}
              title={t('settings.logout')}
            >
              <LogOut size={14} />
            </button>
          </div>
        </div>
      </aside>

      {mobileOpen && (
        <button
          type="button"
          aria-label={t('common.close')}
          className="fixed inset-0 z-40 bg-ink/40 backdrop-blur-sm md:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        {/* TOPBAR */}
        <header className="sticky top-0 z-30 border-b border-border/70 bg-bg/75 backdrop-blur-xl">
          <div className="flex h-[4.5rem] items-center justify-between gap-4 px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-3">
              <button
                type="button"
                className="cursor-pointer rounded-full p-2 text-muted transition-colors hover:bg-faint md:hidden"
                onClick={() => setMobileOpen(true)}
                aria-label="Menu"
              >
                <Menu size={20} />
              </button>
              <div>
                <p className="text-[0.62rem] font-medium uppercase tracking-[0.2em] text-muted">
                  {t('common.appName')}
                </p>
                <p className="display text-2xl leading-none">{t('admin.title')}</p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <LanguageSwitcher />
              <ThemeToggle />
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto px-4 py-8 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-7xl">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
